import {
  Accordion,
  AccordionVariant,
  ColorClass,
  Icon,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import { isEmpty, isNil, isNull } from 'lodash';
import { selectMap3dWorkspace } from 'map-3d/shared/map-3d-slices';
import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { orgAdminRoutes } from 'shared/components/protected-route';
import { GlobalContext } from 'shared/context';
import { isOrgAdmin } from 'shared/helpers';
import { NavItemName } from 'src/home/helpers/enums';
import type { NavItemData, NavItemGroup } from '../types';

type AccordionSideNavTypes = {
  navItems: NavItemGroup[];
  onClickSideNavItem: (navItemData: NavItemData) => void;
};

export const AccordionSideNavItems: React.FC<AccordionSideNavTypes> = ({
  navItems,
  onClickSideNavItem,
}) => {
  // States.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () =>
      navItems.reduce(
        (acc, group) => ({
          ...acc,
          [group.itemType]: group.defaultExpanded ?? false,
        }),
        {},
      ),
  );

  // Selectors.
  const { drawingTools } = useAppSelector(selectMap3dWorkspace);

  // Contexts.
  const { loggedUser } = useContext(GlobalContext);

  // Hooks.
  const location = useLocation();

  // Handlers.
  const handleToggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const shouldHideItem = (item: NavItemData): boolean => {
    const { route, enableForFeature, includeUserRole } = item;

    // Hide nav item if route is restricted to org admins.
    if (
      !isOrgAdmin(loggedUser) &&
      !isNull(route) &&
      orgAdminRoutes.includes(route)
    ) {
      return true;
    }

    // Hide item if required feature flag is disabled.
    if (
      !isNil(enableForFeature) &&
      !isNil(loggedUser) &&
      !loggedUser.featureFlags[enableForFeature]
    ) {
      return true;
    }

    // Hide item if user role is not permitted.
    if (
      !isEmpty(includeUserRole) &&
      !isNil(includeUserRole) &&
      !isNil(loggedUser) &&
      !includeUserRole.includes(loggedUser.type)
    ) {
      return true;
    }

    return false;
  };

  const onClickNavigateTo = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: NavItemData,
  ) => {
    const hasUnsavedFeatures =
      drawingTools?.checkUnsavedFeatures() &&
      item.itemName !== NavItemName.Workspace;

    if (hasUnsavedFeatures) {
      event.preventDefault();
    }

    onClickSideNavItem(item);
  };

  const isItemActive = (item: NavItemData) =>
    item.itemKeys.some((key) => location.pathname.includes(key));

  // Renderers.
  const renderNavItem = (item: NavItemData) => (
    <Link
      to={isNull(item.route) ? {} : item.route}
      key={item.itemName}
      className="text-decoration-none"
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
        onClickNavigateTo(event, item)
      }
    >
      <li
        className={
          isItemActive(item)
            ? 'accordion-sidenav-item active'
            : 'accordion-sidenav-item'
        }
      >
        <Icon
          className="accordion-sidenav-item__item-icon"
          identifier={item.iconIdentifier}
          size={22}
        />

        <span className="accordion-sidenav-item__text">{item.itemName}</span>
      </li>
    </Link>
  );

  const renderAccordionGroup = (group: NavItemGroup) => {
    const { children, itemType, groupIcon } = group;

    const visibleChildren =
      children?.filter((item) => !shouldHideItem(item)) ?? [];

    if (isEmpty(visibleChildren)) {
      return null;
    }

    return (
      <Accordion
        variant={AccordionVariant.ChevronRight}
        activeKey={expandedGroups[itemType] ? itemType : null}
        onSelect={() => handleToggleGroup(itemType)}
        className="accordion-sidenav-group"
      >
        <Accordion.Item
          eventKey={itemType}
          title={
            <div className="accordion-sidenav-item__header">
              {groupIcon && (
                <Icon
                  identifier={groupIcon}
                  colorClass={ColorClass.Primary700}
                  size={22}
                />
              )}

              <span>{itemType}</span>
            </div>
          }
        >
          <div className="accordion-sidenav-group__content">
            {visibleChildren.map((item) => renderNavItem(item))}
          </div>
        </Accordion.Item>
      </Accordion>
    );
  };

  const renderRegularGroup = (group: NavItemGroup) => {
    const visibleChildren =
      group.children?.filter((item: NavItemData) => !shouldHideItem(item)) ??
      [];

    if (isEmpty(visibleChildren)) {
      return null;
    }

    return (
      <div className="accordion-sidenav-item-container__group">
        {visibleChildren.map((item: NavItemData) => renderNavItem(item))}
      </div>
    );
  };

  return (
    <div className="accordion-sidenav-item-container">
      {navItems.map((group: NavItemGroup, index) => {
        const { parentItem, children, itemType } = group;

        const hasVisibleParent =
          !isNil(parentItem) && !shouldHideItem(parentItem);

        const hasVisibleChildren =
          children?.some((item: NavItemData) => !shouldHideItem(item)) ?? false;

        if (!hasVisibleParent && !hasVisibleChildren) {
          return null;
        }

        const isGroupExpandable = !isNil(parentItem) && !isEmpty(children);

        return (
          <div key={itemType}>
            {index !== 0 && <hr className="accordion-sidenav-item__divider" />}

            {hasVisibleParent && <>{renderNavItem(parentItem)}</>}

            {isGroupExpandable
              ? renderAccordionGroup(group)
              : renderRegularGroup(group)}
          </div>
        );
      })}

      <hr className="accordion-sidenav-item__divider" />
    </div>
  );
};
